import { getSession, signIn } from 'next-auth/react'

// NOTE: use STATIC process.env.NEXT_PUBLIC_* access — Next.js inlines these at
// build time only for literal member access (computed `process.env[key]` is NOT
// inlined and breaks the deployed app). The widget bundle is kept safe by the
// `window.process` banner shim in vite.widget.config.ts, so this can't crash there.
const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8001'
const BASE_PATH = process.env.NEXT_PUBLIC_BASE_PATH || ''

// Helper function to check if token is about to expire
function isTokenExpiringSoon(session: { accessTokenExpires?: unknown }): boolean {
  if (!session?.accessTokenExpires) return false
  const expiresAt = session.accessTokenExpires as number
  const now = Date.now()
  const timeUntilExpiry = expiresAt - now
  // Refresh if token expires in less than 2 minutes
  const bufferMs = 2 * 60 * 1000
  return timeUntilExpiry < bufferMs
}

// Track if we've already shown a refresh notification to avoid spam
let hasShownRefreshNotification = false

/**
 * A robust API client that handles authentication, token refresh, and automatic retry.
 * 
 * Features:
 * - Automatic token refresh when token is about to expire
 * - Automatic retry on 401 errors (after token refresh)
 * - Graceful session error handling
 * - Silent token refresh (no user interruption)
 * 
 * @param endpoint The API endpoint to call, e.g., '/api/test' or '/api/admin/dashboard'.
 *                 The endpoint should include the '/api' prefix.
 * @param options Standard fetch options (method, body, etc.).
 * @param retryCount Internal parameter to prevent infinite retry loops.
 */
async function apiClientFetch<T = unknown>(
  endpoint: string, 
  options: RequestInit = {}, 
  retryCount: number = 0
): Promise<T> {
  const maxRetries = 1 // Only retry once to avoid infinite loops
  
  let session = await getSession()

  // Handle session errors that occurred during a token refresh attempt
  if (session?.error === 'RefreshAccessTokenError') {
    console.error('[API Client] Session refresh failed, redirecting to sign-in')
    hasShownRefreshNotification = false // Reset notification flag
    await signIn('keycloak', { callbackUrl: window.location.pathname })
    throw new Error('Session expired. Please sign in again.')
  }

  // Proactive token refresh: If token is expiring soon, force a session refresh
  if (session && isTokenExpiringSoon(session)) {
    console.log('[API Client] Token expiring soon, refreshing session proactively...')
    // Show a one-time notification (don't spam the user)
    if (!hasShownRefreshNotification) {
      console.log('[API Client] Refreshing your session...')
      hasShownRefreshNotification = true
      // Reset the flag after 5 minutes so future refreshes can show notification
      setTimeout(() => { hasShownRefreshNotification = false }, 5 * 60 * 1000)
    }
    
    // Trigger NextAuth to refresh the token by getting a fresh session
    // This calls the jwt() callback which will refresh the token if needed
    session = await getSession()
    
    // Check if refresh succeeded
    if (session?.error === 'RefreshAccessTokenError') {
      console.error('[API Client] Proactive refresh failed, redirecting to sign-in')
      await signIn('keycloak', { callbackUrl: window.location.pathname })
      throw new Error('Session expired. Please sign in again.')
    }
  }

  const headers = new Headers(options.headers || {})

  if (session?.accessToken) {
    headers.set('Authorization', `Bearer ${session.accessToken}`)
  }

  // Construct the full URL
  const fullUrl = `${API_URL}${BASE_PATH}${endpoint}`

  const response = await fetch(fullUrl, { ...options, headers })

  // Handle 401 Unauthorized - Token expired or invalid
  if (response.status === 401) {
    console.warn(`[API Client] Received 401 Unauthorized for ${endpoint}`)
    
    // If this is our first attempt, try to refresh the token and retry
    if (retryCount < maxRetries) {
      console.log('[API Client] Attempting to refresh token and retry...')
      
      // Force NextAuth to refresh the session
      // This will call the jwt() callback which handles token refresh
      const refreshedSession = await getSession()
      
      // Check if refresh was successful
      if (refreshedSession?.error === 'RefreshAccessTokenError') {
        console.error('[API Client] Token refresh failed after 401, redirecting to sign-in')
        hasShownRefreshNotification = false
        await signIn('keycloak', { callbackUrl: window.location.pathname })
        throw new Error('Your session has expired. Please sign in again.')
      }
      
      if (!refreshedSession?.accessToken) {
        console.error('[API Client] No access token after refresh, redirecting to sign-in')
        await signIn('keycloak', { callbackUrl: window.location.pathname })
        throw new Error('Your session has expired. Please sign in again.')
      }
      
      // Retry the request with the new token
      console.log('[API Client] Token refreshed successfully, retrying request...')
      return apiClientFetch<T>(endpoint, options, retryCount + 1)
    }
    
    // If retry also failed, redirect to sign-in
    console.error('[API Client] Retry failed after 401, redirecting to sign-in')
    hasShownRefreshNotification = false
    await signIn('keycloak', { callbackUrl: window.location.pathname })
    throw new Error('Your session has expired. Please sign in again.')
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      detail: response.statusText,
    }))
    throw new Error(errorData.detail || 'An API error occurred.')
  }

  // Handle responses with no content
  if (response.status === 204) {
    return null as T
  }

  return response.json() as Promise<T>
}

/**
 * API client with convenience methods for common HTTP operations.
 */
export const apiClient = {
  /**
   * Perform a GET request.
   */
  get: <T = unknown>(endpoint: string, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, { ...options, method: 'GET' })
  },

  /**
   * Perform a POST request.
   */
  post: <T = unknown>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> => {
    // Check if data is FormData (for file uploads)
    const isFormData = data instanceof FormData
    
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'POST',
      headers: isFormData ? {
        // Don't set Content-Type for FormData - browser sets it with boundary
        ...options?.headers,
      } : {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: isFormData ? data as FormData : (data ? JSON.stringify(data) : undefined),
    })
  },

  /**
   * Perform a PUT request.
   */
  put: <T = unknown>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> => {
    // Check if data is FormData (for file uploads)
    const isFormData = data instanceof FormData
    
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'PUT',
      headers: isFormData ? {
        // Don't set Content-Type for FormData - browser sets it with boundary
        ...options?.headers,
      } : {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: isFormData ? data as FormData : (data ? JSON.stringify(data) : undefined),
    })
  },

  /**
   * Perform a PATCH request.
   */
  patch: <T = unknown>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> => {
    // Check if data is FormData (for file uploads)
    const isFormData = data instanceof FormData
    
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'PATCH',
      headers: isFormData ? {
        // Don't set Content-Type for FormData - browser sets it with boundary
        ...options?.headers,
      } : {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: isFormData ? data as FormData : (data ? JSON.stringify(data) : undefined),
    })
  },

  /**
   * Perform a DELETE request.
   */
  delete: <T = unknown>(endpoint: string, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, { ...options, method: 'DELETE' })
  },
}

// Default export for backward compatibility
export default apiClientFetch
